import { Component, forwardRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { PrFormSelectListComponent } from '@app/components/pr-form/pr-form-select-list/pr-form-select-list.component';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { PrSubformComponent } from '@app/components/pr-form/pr-subform/pr-subform.component';
import { PrTableFormComponent } from '@app/components/pr-form/pr-table-form/pr-table-form.component';
import { PrTableColumn } from '@app/components/pr-table/pr-table.component';
import { ReferenceDao } from '@app/dao/reference/reference.dao';
import { HttpApiService } from '@app/providers/http-api/http-api.service';
import * as _ from 'lodash';
import { combineLatest, Observable, Subject, debounceTime, map, mergeMap, tap } from 'rxjs';
import { EaSimulationAvPackFormBuilder } from './ea-simulation-av-pack-form.builder';

@Component({
    selector: 'ea-simulation-av-pack-form',
    templateUrl: './ea-simulation-av-pack-form.component.html',
    styleUrls: ['./ea-simulation-av-pack-form.component.scss'],
    providers: [
        {
            provide: PrFormAbstract,
            useExisting: forwardRef(() => EaSimulationAvPackFormComponent),
        },
    ],
    standalone: false
})
export class EaSimulationAvPackFormComponent
  extends EaSimulationAvPackFormBuilder
  implements OnInit
{
  _ = _;
  doneSubject: Subject<void> = new Subject<void>();

  criteriaForm: UntypedFormGroup;

  supports: any[] = [];
  supportTypes = [];
  supportsLength = 0;
  supportsPageSize = 15;
  currentPage = 0;
  tmpProductId;
  hideTable: boolean = true;

  @ViewChild('tableForm', { static: true }) tableForm: PrTableFormComponent;
  @ViewChild('supportsForm', { static: true }) supportsForm: PrSubformComponent;
  @ViewChild('formSelectList', {
    static: true,
    read: PrFormSelectListComponent,
  })
  formSelectList: PrFormSelectListComponent;
  @ViewChild('paginator', { static: false }) paginator: MatPaginator;

  columnsDefinition = [
    new PrTableColumn('', 'action', 'action'),
    new PrTableColumn('Noms des supports', 'name', { span: 3, tooltip: true }),
    new PrTableColumn('Repartition', 'allocationPercentage', 'input', {
      changeCallback: this.tableAllocationColumnValueChanged.bind(this),
      suffix: '%',
      span: 2,
      class: 'allocation-percentage',
      hasTotal: true,
      totalText: true,
    }),
    new PrTableColumn('Montant investi', 'investedAmount', 'currency', {
      span: 2,
      hasTotal: true,
    }),
  ];

  constructor(
    protected formConstructor: PrFormConstructor,
    private httpApi: HttpApiService,
    private referenceDao: ReferenceDao,
  ) {
    super(formConstructor);

    this.criteriaForm = this.formBuilder.group({
      autocomplete: [],
      supportTypes: [],
    });
  }

  ngOnInit(): void {
    this.openSupportsForm();
    this.formSelectList.loading = true;

    combineLatest([this.referenceDao.getSupportTypes(), this.search()])
      .pipe(
        map(
          ([supportTypes, search]) =>
            (this.supportTypes = supportTypes.filter(
              ({ id }) => id != 'STRUCTURED_PRODUCTS',
            )),
        ),
      )
      .subscribe(() => {
        this.formSelectList.loading = false;
      });

    this.criteriaForm.valueChanges
      .pipe(
        tap(() => (this.formSelectList.loading = true)),
        debounceTime(500),
        mergeMap(this.search.bind(this)),
      )
      .subscribe();
  }

  changeSupportsPage(event) {
    this.currentPage = event.pageIndex;
    this.formSelectList.loading = true;
    this.search().subscribe();
  }

  search(): Observable<any> {
    if (this.options.productId) this.tmpProductId = this.options.productId;
    return this.httpApi
      .post(`/supports/search`, {
        active: true,
        page: this.currentPage + 1,
        size: this.supportsPageSize,
        lifeInsuranceId: this.options.productId || this.tmpProductId,
        autocomplete: this.criteriaForm.value.autocomplete,
        supportTypes: this.criteriaForm.value.supportTypes
          ? [this.criteriaForm.value.supportTypes]
          : undefined,
        supportTypeNotIn: ['STRUCTURED_PRODUCTS'],
        isAvailableInSimulationAndSubscription: true,
      })
      .pipe(
        map(res => {
          this.formSelectList.loading = false;
          this.supportsLength = res.totalElements;
          this.supports = res.content;
          return res;
        }),
      );
  }

  _defaultAllocationFormValue: any;
  onSupportSelect(support) {
    let newControl = this.formBuilder.group(this.createModel(support));
    let formArray = this.form.controls.allocation as UntypedFormArray;
    newControl.setValue({ ...newControl.value, index: formArray.value.length });
    formArray.push(newControl);
  }

  resetPage() {
    this.paginator.firstPage();
  }

  onSupportDeselect(support) {
    let formArray = this.form.controls.allocation as UntypedFormArray;
    let control = formArray.controls.findIndex(
      c => c.value.id == support.id || c.value.isin == support.isin,
    );
    formArray.removeAt(control);
  }

  tableAllocationColumnValueChanged(value: any, row: any, column: any) {
    this.allocation.controls[row.index].setValue({
      ...this.allocation.controls[row.index].value,
      [column.name]: value / 100,
    });
    this.markAsDirtyTouchedAndRefresh(row.index);
  }

  cancelManageAllocationform() {
    this.entity = this._defaultAllocationFormValue.allocation;
    this.generate(this.entity, this.options);
    this._defaultAllocationFormValue = [];
  }

  validManageAllocationform() {
    this.newOptionLogic();
    this.tableForm.initTable();
    this._defaultAllocationFormValue = [];
  }

  markAsDirtyTouchedAndRefresh(control) {
    this.allocation.controls[control].markAsDirty();
    this.allocation.controls[control].markAsTouched();
    this.tableForm.initTable();
  }

  formatTableDataFunction() {
    return data =>
      _(data)
        .map((data, i) => {
          return {
            index: i,
            name: data.name,
            allocationPercentage: data.allocationPercentage * 100,
            investedAmount:
              data.totalInvestedAmount * data.allocationPercentage,
            totalInvestedAmount: data.totalInvestedAmount,
          };
        })
        .value();
  }

  newOptionLogic() {
    //First element at 100%
    if (+this.allocation.controls.length == 1)
      return this.allocation.controls[0].patchValue({
        allocationPercentage: 1,
      });

    // Form untouched = even allocation
    if (!this.allocation.dirty) {
      this.allocation.controls[0].patchValue({ allocationPercentage: 0 });

      //Ignore first control in order to set the missing allocation percent inside
      _.forEach(this.allocation.controls, (c, i) =>
        +i != 0
          ? c.patchValue({
              allocationPercentage: this.floored(
                1 / +this.allocation.controls.length,
              ),
            })
          : null,
      );

      let allocationLeft =
        this.totalAllocatedPercent < 1
          ? this.floored(1 - this.totalAllocatedPercent)
          : this.floored(this.totalAllocatedPercent - 1);
      this.allocation.controls[0].patchValue({
        allocationPercentage: allocationLeft,
      });
      return;
    }

    // Allocation < 1 = set value left to the control
    if (this.totalAllocatedPercent < 1) {
      let newControl =
        this.allocation.controls[this.allocation.value.length - 1];
      return newControl.patchValue({
        allocationPercentage: 1 - this.totalAllocatedPercent,
      });
    }
  }

  openSupportsForm() {
    this.supportsForm.open();
  }

  onMoreInfoClick(support) {
    window.open(`#/dashboard/assets/supports/${support.id}`, '_blank');
  }

  onSupportsChange(isData) {
    this.hideTable = isData;
  }

  onSubmit() {
    this.derrors = true;

    // stop here if form is invalid
    if (this.form.invalid || this.totalAllocatedPercent != 1) {
      return;
    }

    this.doneSubject.next();
    if (this.callback) {
      this.callback(this.extract());
    }
  }

  floored(value) {
    return Math.round(value * 100) / 100;
  }

  get allocation() {
    return this.form.get('allocation') as UntypedFormGroup;
  }

  get totalAllocatedPercent() {
    return this.floored(
      _(this.allocation.value).map('allocationPercentage').sum(),
    );
  }
  get allocationPanel() {
    if (
      !this.allocation ||
      !this.allocation.value ||
      this.allocation.value.length == 0
    )
      return {
        display: 'info',
        icon: 'info',
        text: 'Renseignez vos investissements',
      };
    else if (this.totalAllocatedPercent > 1)
      return {
        display: 'danger',
        icon: 'error',
        text: `${Math.round(
          this.totalAllocatedPercent * 100,
        )}% au total - Vous ne pouvez pas repartir plus de 100% de l'investissement`,
      };
    else if (this.totalAllocatedPercent < 1)
      return {
        display: 'warning',
        icon: 'warning',
        text: `Il reste ${Math.round(
          this.form.get('allocation').value[0].totalInvestedAmount *
            (1 - this.totalAllocatedPercent),
        )}€ de votre investissement à répartir, soit ${Math.round(
          (1 - this.totalAllocatedPercent) * 100,
        )}%`,
      };
    else if (
      _.filter(this.allocation.value, support => !support.allocationPercentage)
        .length
    )
      return {
        display: 'danger',
        icon: 'error',
        text: 'Vous ne pouvez pas repartir 0% dans un support',
      };
    else if (this.totalAllocatedPercent == 1)
      return {
        display: 'success',
        icon: 'check',
        text: 'Félicitations, vos investissements sont répartis à 100%',
      };
  }
}
